import React, { useEffect, useRef, useState } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { reconciliationAPI } from '../services/api';
import MismatchPanel from './MismatchPage';

const WS_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

// 값 변경 시 부드럽게 노란색 깜박임 (실시간 효과)
const FlashValue: React.FC<{ value: number | null | undefined; format?: (v: number) => string; color?: string; bold?: boolean }> = ({ value, format, color, bold }) => {
  const [flash, setFlash] = useState(false);
  const prevRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (prevRef.current !== undefined && prevRef.current !== value) {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 600);
      return () => clearTimeout(t);
    }
    prevRef.current = value ?? undefined;
  }, [value]);

  if (value == null) return <span style={{ color: 'rgba(255,255,255,0.3)' }}>-</span>;

  const fmtFn = format || ((v: number) => v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  return (
    <span style={{
      color: flash ? '#ffe066' : (color || 'rgba(255,255,255,0.85)'),
      fontWeight: bold ? 600 : 400,
      transition: 'color 0.4s ease-out',
      fontFamily: 'monospace',
    }}>
      {fmtFn(value as number)}
    </span>
  );
};

// ─── 정렬 훅 + 헤더 컴포넌트 (모든 테이블 공통) ───
type SortDir = 'asc' | 'desc' | null;

function useSort<T>(rows: T[]) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);

  const toggleSort = (key: string) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir('asc');
    } else if (sortDir === 'asc') {
      setSortDir('desc');
    } else if (sortDir === 'desc') {
      setSortKey(null);
      setSortDir(null);
    } else {
      setSortDir('asc');
    }
  };

  const sorted = React.useMemo(() => {
    if (!sortKey || !sortDir) return rows;
    const copy = [...rows];
    copy.sort((a: any, b: any) => {
      let av = a[sortKey];
      let bv = b[sortKey];
      // null 은 항상 뒤로
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      // 숫자 비교 시도
      const an = Number(av), bn = Number(bv);
      if (!isNaN(an) && !isNaN(bn)) {
        return sortDir === 'asc' ? an - bn : bn - an;
      }
      // 문자열 비교
      const as = String(av), bs = String(bv);
      return sortDir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
    });
    return copy;
  }, [rows, sortKey, sortDir]);

  return { sorted, sortKey, sortDir, toggleSort };
}

// 정렬 가능한 th
const SortableTh: React.FC<{
  label: string;
  sortField: string;
  currentKey: string | null;
  currentDir: SortDir;
  onSort: (key: string) => void;
  align?: 'left' | 'right';
}> = ({ label, sortField, currentKey, currentDir, onSort, align = 'left' }) => {
  const active = currentKey === sortField;
  const arrow = active ? (currentDir === 'asc' ? ' ▲' : currentDir === 'desc' ? ' ▼' : '') : '';
  return (
    <th
      onClick={() => onSort(sortField)}
      style={{
        padding: '10px 12px', textAlign: align, fontWeight: 500,
        borderBottom: '1px solid #2a2f4a', whiteSpace: 'nowrap',
        cursor: 'pointer', userSelect: 'none',
        color: active ? '#6c8eef' : '#aaa',
      }}
      title="클릭하여 정렬"
    >
      {label}<span style={{ fontSize: 10 }}>{arrow || ' ⇅'}</span>
    </th>
  );
};

/**
 * 적합성 검증 페이지
 *
 * 3개 서브탭:
 *   1) 오픈 포지션 비교 - 우리 시스템 살아있는 포지션 ↔ 마스터 측 비교
 *   2) 거래 내역 비교 - 모든 거래 (오픈+청산), 기간 선택 가능
 *   3) 좀비 detect - 마스터에만 있고 우리 시스템과 무관한 포지션
 *
 * 좌우 분할 표시:
 *   - 좌: 트레이더 측 (point_hedge_position)
 *   - 우: 마스터 측 (master_open_position)
 *
 * 적합성 상태:
 *   - MATCHED: 일치 (정상)
 *   - MISMATCHED: 우리 시스템에 있는데 마스터 측 NULL/없음
 *   - ZOMBIE: 마스터에만 있고 우리 시스템에 없음
 *   - EXCLUDED: CANCELLED (비교 제외, 정상)
 */

type Tab = 'pending' | 'positions' | 'deals' | 'zombies' | 'mismatch';
type StatusFilter = 'ALL' | 'OPEN' | 'CLOSED';

interface AccountOption { account_id: string; }
interface SymbolOption { symbol: string; }
interface RuleOption { id: number; name: string; master_account_id: string; account_count: number; }

interface PositionRow {
  reconciliation_status: 'MATCHED' | 'MISMATCHED' | 'MISMATCHED_CLOSED_AT_MASTER' | 'ZOMBIE' | 'EXCLUDED';
  // 트레이더 측 (우리 시스템)
  our_id?: number;
  account_id?: string;
  customer_ticket?: number;
  master_ticket?: number | null;
  symbol?: string;
  direction?: string;
  lots?: number;
  customer_open_price?: number;
  master_open_price?: number;
  entry_gap_point?: number;
  entry_type?: string;
  status?: string;
  opened_at?: string;
  closed_at?: string;
  customer_profit?: number;
  master_profit?: number;
  agent_cost?: number;
  broker_profit?: number;
  remaining_lots?: number;
  // 마스터 측 (master_open_position)
  mop_ticket?: number | null;
  mop_open_price?: number;
  mop_lots?: number;
  mop_current_price?: number;
  mop_profit?: number;
  mop_comment?: string;
  master_account_id?: string;
  // 실시간 customer 데이터 (account_open_position)
  customer_floating_profit?: number;
  customer_current_price?: number;
}

interface ZombieRow {
  ticket: number;
  master_account_id: string;
  symbol: string;
  direction: string;
  lots: number;
  open_price: number;
  current_price: number;
  profit: number;
  swap: number;
  comment: string;
  open_time: string;
  updated_at: string;
  extracted_account_id?: string;
  extracted_tag?: string;
  extracted_customer_ticket?: number;
}

interface Summary {
  matched: number;
  mismatched: number;
  excluded?: number;
  zombie?: number;
  totalOur?: number;
  total?: number;
  sumCustomerPnl?: number;
  sumMasterPnl?: number;
  sumAgentCost?: number;
  sumBrokerPnl?: number;
}

const ReconciliationPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('positions');

  // 필터
  const [ruleId, setRuleId] = useState<string>('');
  const [accountId, setAccountId] = useState('');
  const [symbol, setSymbol] = useState('');
  const [onlyMismatch, setOnlyMismatch] = useState(false);
  const [includeCancelled, setIncludeCancelled] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // 페이징
  const [page, setPage] = useState(0);
  // 서버 정렬 (전체 데이터 정렬)
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc' | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false); // 자동 새로고침 (기본 OFF - 정렬 시 방해 안 되게)
  const [mismatchSummary, setMismatchSummary] = useState<any>({}); // 헷지 불일치 KPI (자식이 올려줌)
  const handleSort = (key: string) => {
    if (sortBy !== key) { setSortBy(key); setSortDir('asc'); setPage(0); }
    else if (sortDir === 'asc') { setSortDir('desc'); setPage(0); }
    else { setSortBy(null); setSortDir(null); setPage(0); }
  };
  const pageSize = 50;

  // 데이터
  const [rows, setRows] = useState<PositionRow[]>([]);
  const [zombieRows, setZombieRows] = useState<ZombieRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState<Summary>({ matched: 0, mismatched: 0 });
  const [loading, setLoading] = useState(false);
  const [rules, setRules] = useState<RuleOption[]>([]);
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [symbols, setSymbols] = useState<SymbolOption[]>([]);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);

  // 필터 옵션 로드 - 룰과 심볼은 1회만, 계좌는 ruleId 변경 시마다
  useEffect(() => {
    reconciliationAPI.getRules().then(r => setRules(r.data || [])).catch(() => {});
    reconciliationAPI.getSymbols().then(r => setSymbols(r.data || [])).catch(() => {});
  }, []);

  // ruleId 가 바뀌면 그 룰의 계좌만 로드 + 계좌 선택 초기화
  useEffect(() => {
    const params = ruleId ? { ruleId } : undefined;
    reconciliationAPI.getAccounts(params).then(r => setAccounts(r.data || [])).catch(() => {});
    setAccountId(''); // 룰 바뀌면 계좌 선택 초기화
  }, [ruleId]);

  // 탭 또는 필터 변경 시 page 0 으로
  useEffect(() => {
    setPage(0);
    setSortBy(null);
    setSortDir(null);
  }, [tab, ruleId, accountId, symbol, onlyMismatch, includeCancelled, statusFilter, fromDate, toDate]);

  // 데이터 페치
  const fetchData = async () => {
    setLoading(true);
    try {
      if (tab === 'pending') {
        const params: any = { page, size: pageSize };
        if (ruleId) params.ruleId = ruleId;
        if (accountId) params.accountId = accountId;
        if (symbol) params.symbol = symbol;
        if (onlyMismatch) params.onlyMismatch = true;
        if (sortBy) { params.sortBy = sortBy; params.sortDir = sortDir; }
        const r = await reconciliationAPI.getPending(params);
        setRows(r.data.rows || []);
        setTotalCount(r.data.totalCount || 0);
        setSummary(r.data.summary || { matched: 0, mismatched: 0 });
      } else if (tab === 'positions') {
        const params: any = { page, size: pageSize };
        if (ruleId) params.ruleId = ruleId;
        if (accountId) params.accountId = accountId;
        if (symbol) params.symbol = symbol;
        if (onlyMismatch) params.onlyMismatch = true;
        if (sortBy) { params.sortBy = sortBy; params.sortDir = sortDir; }
        const r = await reconciliationAPI.getPositions(params);
        setRows(r.data.rows || []);
        setTotalCount(r.data.totalCount || 0);
        setSummary(r.data.summary || { matched: 0, mismatched: 0 });
      } else if (tab === 'deals') {
        const params: any = { page, size: pageSize, statusFilter, includeCancelled };
        if (ruleId) params.ruleId = ruleId;
        if (accountId) params.accountId = accountId;
        if (symbol) params.symbol = symbol;
        if (onlyMismatch) params.onlyMismatch = true;
        if (fromDate) params.fromDate = fromDate;
        if (toDate) params.toDate = toDate;
        if (sortBy) { params.sortBy = sortBy; params.sortDir = sortDir; }
        const r = await reconciliationAPI.getDeals(params);
        setRows(r.data.rows || []);
        setTotalCount(r.data.totalCount || 0);
        setSummary(r.data.summary || { matched: 0, mismatched: 0 });
      } else if (tab === 'zombies') {
        const params: any = { page, size: pageSize };
        if (ruleId) params.ruleId = ruleId;
        if (accountId) params.accountId = accountId;
        if (symbol) params.symbol = symbol;
        if (sortBy) { params.sortBy = sortBy; params.sortDir = sortDir; }
        const r = await reconciliationAPI.getZombies(params);
        setZombieRows(r.data.rows || []);
        setTotalCount(r.data.totalCount || 0);
        // 좀비 탭 전용 summary - 이전 탭 값 안 섞이게 명확히 초기화
        setSummary({ matched: 0, mismatched: 0, zombie: r.data.totalCount || 0 });
      }
      setLastFetchedAt(new Date());
    } catch (e: any) {
      console.error('[RECONCILE] 페치 오류', e);
      alert('데이터 로드 실패: ' + (e?.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, page, ruleId, accountId, symbol, onlyMismatch, includeCancelled, statusFilter, fromDate, toDate, sortBy, sortDir]);

  // 오픈 포지션 탭: autoRefresh 켜졌을 때만 5초 polling + STOMP 실시간
  useEffect(() => {
    if (tab !== 'positions') return;
    if (!autoRefresh) return; // 자동 새로고침 OFF 면 polling 안 함 (정렬 방해 방지)

    const intervalId = setInterval(() => {
      fetchData();
    }, 5000);

    const stompClient = new Client({
      webSocketFactory: () => new SockJS(`${WS_BASE}/api/ws`),
      reconnectDelay: 5000,
      onConnect: () => {
        stompClient.subscribe('/topic/equity-updates', () => {
          fetchData();
        });
      },
    });
    stompClient.activate();

    return () => {
      clearInterval(intervalId);
      stompClient.deactivate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, ruleId, accountId, symbol, onlyMismatch, autoRefresh]);

  const totalPages = Math.ceil(totalCount / pageSize);

  // ─── 스타일 헬퍼 ───────────────────────────────
  const fmtNum = (v: any, digits = 2) => {
    if (v == null || v === '') return '-';
    const n = Number(v);
    if (isNaN(n)) return '-';
    return n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
  };
  const fmtDate = (s?: string) => {
    if (!s) return '-';
    return s.substring(0, 19).replace('T', ' ');
  };

  const statusBadge = (status: string) => {
    const colors: any = {
      MATCHED: { bg: '#1a3a26', color: '#4ade80', label: '일치' },
      PENDING_REGISTRATION: { bg: '#3a2a1a', color: '#fbbf24', label: '등록 중' },
      MISMATCHED: { bg: '#3a1a1a', color: '#f87171', label: '불일치' },
      MISMATCHED_CLOSED_AT_MASTER: { bg: '#3a1a1a', color: '#f87171', label: '마스터청산' },
      ZOMBIE: { bg: '#3a2a1a', color: '#fbbf24', label: '좀비' },
      EXCLUDED: { bg: '#2a2a2a', color: '#888', label: '제외' }
    };
    const c = colors[status] || colors.MATCHED;
    return (
      <span style={{
        background: c.bg, color: c.color,
        padding: '2px 8px', borderRadius: 4,
        fontSize: 11, fontWeight: 500
      }}>{c.label}</span>
    );
  };

  // ─── 렌더 ───────────────────────────────────────
  return (
    <div style={{ padding: 20, background: '#0f0f1a', color: '#e0e0e0', minHeight: '100vh' }}>
      {/* 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, color: '#fff' }}>🔍 적합성 검증</h2>
          {ruleId && (
            <div style={{ fontSize: 12, color: '#a78bfa', marginTop: 4 }}>
              {rules.find(r => String(r.id) === String(ruleId))?.name} ·
              마스터 {rules.find(r => String(r.id) === String(ruleId))?.master_account_id}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13, color: '#888' }}>
          <span>마지막 갱신: {lastFetchedAt ? lastFetchedAt.toLocaleTimeString() : '-'}</span>
          {tab === 'positions' && (
            <button onClick={() => setAutoRefresh(v => !v)} style={{
              background: autoRefresh ? 'rgba(74,222,128,0.15)' : '#1a1f3a',
              color: autoRefresh ? '#4ade80' : '#888',
              border: `1px solid ${autoRefresh ? 'rgba(74,222,128,0.4)' : '#2a2f4a'}`,
              padding: '6px 14px', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600
            }}>
              {autoRefresh ? '⏸ 자동 새로고침 ON' : '▶ 자동 새로고침 OFF'}
            </button>
          )}
          <button onClick={fetchData} disabled={loading} style={{
            background: '#1a1f3a', color: '#e0e0e0', border: '1px solid #2a2f4a',
            padding: '6px 14px', borderRadius: 4, cursor: 'pointer', fontSize: 13
          }}>
            {loading ? '⏳ 로딩...' : '🔄 새로고침'}
          </button>
        </div>
      </div>

      {/* KPI 카드 - 모든 탭 공통 (헷지 불일치도 좀비처럼 탭 위에 표시) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {/* 헷지 불일치 탭 전용 KPI */}
        {tab === 'mismatch' && (
          <>
            <div style={{ background: '#3a1a1a', padding: '14px 18px', borderRadius: 6 }}>
              <div style={{ fontSize: 12, color: '#f87171', marginBottom: 6 }}>🚨 마스터 진입 누락</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#f87171' }}>{mismatchSummary.entry_missing ?? 0}</div>
            </div>
            <div style={{ background: '#3a2a1a', padding: '14px 18px', borderRadius: 6 }}>
              <div style={{ fontSize: 12, color: '#fbbf24', marginBottom: 6 }}>⚠️ 마스터 청산 누락</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#fbbf24' }}>{mismatchSummary.close_missing ?? 0}</div>
            </div>
            <div style={{ background: '#2a1a3a', padding: '14px 18px', borderRadius: 6 }}>
              <div style={{ fontSize: 12, color: '#a78bfa', marginBottom: 6 }}>⚠️ 부분 청산 불일치</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#a78bfa' }}>{mismatchSummary.partial_mismatch ?? 0}</div>
            </div>
            <div style={{ background: '#1a3a26', padding: '14px 18px', borderRadius: 6 }}>
              <div style={{ fontSize: 12, color: '#4ade80', marginBottom: 6 }}>✅ 해결됨 (누적)</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#4ade80' }}>{mismatchSummary.total_resolved ?? 0}</div>
            </div>
          </>
        )}
        {tab !== 'mismatch' && tab !== 'zombies' && (
          <div style={{ background: '#1a3a26', padding: '14px 18px', borderRadius: 6 }}>
            <div style={{ fontSize: 12, color: '#4ade80', marginBottom: 6 }}>일치</div>
            <div style={{ fontSize: 22, fontWeight: 500, color: '#4ade80' }}>{summary.matched}</div>
          </div>
        )}
        {tab !== 'zombies' && tab !== 'mismatch' && (
          <div style={{ background: '#3a1a1a', padding: '14px 18px', borderRadius: 6 }}>
            <div style={{ fontSize: 12, color: '#f87171', marginBottom: 6 }}>불일치</div>
            <div style={{ fontSize: 22, fontWeight: 500, color: '#f87171' }}>{summary.mismatched}</div>
          </div>
        )}
        {tab === 'positions' && (
          <div style={{ background: '#3a2a1a', padding: '14px 18px', borderRadius: 6 }}>
            <div style={{ fontSize: 12, color: '#fbbf24', marginBottom: 6 }}>좀비 (마스터 잔존)</div>
            <div style={{ fontSize: 22, fontWeight: 500, color: '#fbbf24' }}>{summary.zombie ?? 0}</div>
          </div>
        )}
        {tab === 'pending' && (
          <div style={{ background: '#3a1a1a', padding: '14px 18px', borderRadius: 6 }}>
            <div style={{ fontSize: 12, color: '#f87171', marginBottom: 6 }}>Limit 등록 실패 (30초 초과)</div>
            <div style={{ fontSize: 22, fontWeight: 500, color: '#f87171' }}>{summary.mismatched ?? 0}</div>
          </div>
        )}
        {tab === 'deals' && (
          <div style={{ background: '#2a2a2a', padding: '14px 18px', borderRadius: 6 }}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>취소됨 (비교 제외)</div>
            <div style={{ fontSize: 22, fontWeight: 500, color: '#aaa' }}>{summary.excluded ?? 0}</div>
          </div>
        )}
        {tab === 'zombies' && (
          <div style={{ background: '#3a2a1a', padding: '14px 18px', borderRadius: 6 }}>
            <div style={{ fontSize: 12, color: '#fbbf24', marginBottom: 6 }}>좀비 총합</div>
            <div style={{ fontSize: 22, fontWeight: 500, color: '#fbbf24' }}>{totalCount}</div>
          </div>
        )}
        {tab !== 'mismatch' && (
          <div style={{ background: '#1a1f3a', padding: '14px 18px', borderRadius: 6 }}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>전체</div>
            <div style={{ fontSize: 22, fontWeight: 500 }}>{totalCount}</div>
          </div>
        )}
      </div>

      {/* PnL 합계 KPI (오픈 포지션 + 거래 내역 탭에만) */}
      {(tab === 'positions' || tab === 'deals') && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: tab === 'deals' ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)',
          gap: 12, marginBottom: 20
        }}>
          <div style={pnlCardStyle}>
            <div style={pnlCardLabel}>
              트레이더 PnL {tab === 'positions' && <span style={liveBadge}>● LIVE</span>}
            </div>
            <div style={{ fontSize: 24, fontWeight: 600 }}>
              <FlashValue
                value={summary.sumCustomerPnl ?? 0}
                format={(v) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                color={(summary.sumCustomerPnl ?? 0) >= 0 ? '#4ade80' : '#f87171'}
                bold
              />
            </div>
          </div>
          <div style={pnlCardStyle}>
            <div style={pnlCardLabel}>
              마스터 PnL {tab === 'positions' && <span style={liveBadge}>● LIVE</span>}
            </div>
            <div style={{ fontSize: 24, fontWeight: 600 }}>
              <FlashValue
                value={summary.sumMasterPnl ?? 0}
                format={(v) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                color={(summary.sumMasterPnl ?? 0) >= 0 ? '#4ade80' : '#f87171'}
                bold
              />
            </div>
          </div>
          {tab === 'deals' && (
            <div style={pnlCardStyle}>
              <div style={pnlCardLabel}>Agent 비용</div>
              <div style={{ fontSize: 24, fontWeight: 600, color: '#fbbf24', fontFamily: 'monospace' }}>
                {(summary.sumAgentCost ?? 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2, maximumFractionDigits: 2
                })}
              </div>
            </div>
          )}
          <div style={{ ...pnlCardStyle, background: 'linear-gradient(135deg, rgba(102,126,234,0.15), rgba(118,75,162,0.15))', border: '1px solid rgba(102,126,234,0.3)' }}>
            <div style={pnlCardLabel}>
              Broker 수익
              {tab === 'positions' ? (
                <span style={{ ...liveBadge, color: '#fbbf24' }}>● LIVE (Agent 제외)</span>
              ) : (
                <span style={{ fontSize: 11, color: '#a78bfa', marginLeft: 8 }}>(확정, Agent 포함)</span>
              )}
            </div>
            <div style={{ fontSize: 26, fontWeight: 700 }}>
              <FlashValue
                value={summary.sumBrokerPnl ?? 0}
                format={(v) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                color={(summary.sumBrokerPnl ?? 0) >= 0 ? '#4ade80' : '#f87171'}
                bold
              />
            </div>
          </div>
        </div>
      )}

      {/* 탭 */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid #2a2f4a' }}>
        {([
          ['pending', '📌 예약중 (Limit)'],
          ['positions', '📍 오픈 포지션 비교'],
          ['deals', '📋 거래 내역 비교'],
          ['zombies', '👻 좀비 detect'],
          ['mismatch', '🚨 헷지 불일치']
        ] as [Tab, string][]).map(([t, label]) => (
          <div key={t} onClick={() => setTab(t)} style={{
            padding: '10px 20px', cursor: 'pointer',
            borderBottom: tab === t ? '2px solid #6c8eef' : '2px solid transparent',
            color: tab === t ? '#6c8eef' : '#888',
            fontWeight: tab === t ? 500 : 400, fontSize: 14
          }}>{label}</div>
        ))}
      </div>

      {/* 필터 영역 (헷지 불일치 탭은 자체 필터 사용) */}
      {tab !== 'mismatch' && (
      <div style={{
        display: 'flex', gap: 12, marginBottom: 16, padding: 12,
        background: '#1a1f3a', borderRadius: 6, alignItems: 'center', flexWrap: 'wrap'
      }}>
        <select value={ruleId} onChange={e => setRuleId(e.target.value)} style={selectStyle}>
          <option value="">전체 룰</option>
          {rules.map(r => (
            <option key={r.id} value={r.id}>
              {r.name} (마스터 {r.master_account_id} · {r.account_count}계좌)
            </option>
          ))}
        </select>
        <select value={accountId} onChange={e => setAccountId(e.target.value)} style={selectStyle}>
          <option value="">{ruleId ? '룰의 전체 계좌' : '전체 계좌'}</option>
          {accounts.map(a => <option key={a.account_id} value={a.account_id}>{a.account_id}</option>)}
        </select>
        <select value={symbol} onChange={e => setSymbol(e.target.value)} style={selectStyle}>
          <option value="">전체 심볼</option>
          {symbols.map(s => <option key={s.symbol} value={s.symbol}>{s.symbol}</option>)}
        </select>

        {tab === 'deals' && (
          <>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={inputStyle} />
            <span style={{ color: '#888' }}>~</span>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={inputStyle} />
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={includeCancelled} onChange={e => setIncludeCancelled(e.target.checked)} />
              취소됨 포함
            </label>
          </>
        )}

        {(tab === 'positions' || tab === 'deals') && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input type="checkbox" checked={onlyMismatch} onChange={e => setOnlyMismatch(e.target.checked)} />
            불일치만 보기
          </label>
        )}

        <div style={{ marginLeft: 'auto', fontSize: 13, color: '#888' }}>
          {totalCount.toLocaleString()}개
        </div>
      </div>

      )}

      {/* 본문 테이블 */}
      {tab === 'pending' && <PendingTable rows={rows} fmtNum={fmtNum} fmtDate={fmtDate} statusBadge={statusBadge} sortKey={sortBy} sortDir={sortDir} toggleSort={handleSort} />}
      {tab === 'positions' && <PositionTable rows={rows} fmtNum={fmtNum} fmtDate={fmtDate} statusBadge={statusBadge} sortKey={sortBy} sortDir={sortDir} toggleSort={handleSort} />}
      {tab === 'deals' && <DealsTable rows={rows} fmtNum={fmtNum} fmtDate={fmtDate} statusBadge={statusBadge} sortKey={sortBy} sortDir={sortDir} toggleSort={handleSort} />}
      {tab === 'zombies' && <ZombieTable rows={zombieRows} fmtNum={fmtNum} fmtDate={fmtDate} sortKey={sortBy} sortDir={sortDir} toggleSort={handleSort} />}
      {tab === 'mismatch' && <MismatchPanel embedded hideKpi onSummaryChange={setMismatchSummary} />}

      {/* 페이징 (헷지 불일치 탭 제외) */}
      {tab !== 'mismatch' && totalPages > 1 && (
        <div style={{
          display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16, alignItems: 'center'
        }}>
          <button onClick={() => setPage(0)} disabled={page === 0} style={pageBtnStyle}>«</button>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={pageBtnStyle}>‹</button>
          <span style={{ fontSize: 13, color: '#aaa' }}>
            {page + 1} / {totalPages}
          </span>
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={pageBtnStyle}>›</button>
          <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1} style={pageBtnStyle}>»</button>
        </div>
      )}
    </div>
  );
};

// ─── 예약중 Limit Order 테이블 (좌우 분할) ──────────────────────────────
const PendingTable: React.FC<{
  rows: PositionRow[];
  fmtNum: (v: any, d?: number) => string;
  fmtDate: (s?: string) => string;
  statusBadge: (s: string) => React.ReactNode;
  sortKey: string | null;
  sortDir: 'asc' | 'desc' | null;
  toggleSort: (k: string) => void;
}> = ({ rows, fmtNum, fmtDate, statusBadge, sortKey, sortDir, toggleSort }) => {
  const sorted = rows; // 서버 정렬됨

  if (rows.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
        예약중인 Limit Order 가 없습니다
      </div>
    );
  }
  const hdrClick = (k: string): React.CSSProperties => ({
    cursor: 'pointer', userSelect: 'none',
    color: sortKey === k ? '#6c8eef' : undefined,
  });
  const arrow = (k: string) => sortKey === k ? (sortDir === 'asc' ? ' ▲' : sortDir === 'desc' ? ' ▼' : '') : ' ⇅';
  return (
    <div style={{ background: '#1a1f3a', borderRadius: 6, overflow: 'hidden' }}>
      <div style={headerRowStyle}>
        <div style={{ width: 60, ...hdrClick('reconciliation_status') }} onClick={() => toggleSort('reconciliation_status')}>상태<span style={{fontSize:10}}>{arrow('reconciliation_status')}</span></div>
        <div style={{ width: 100, ...hdrClick('customer_ticket') }} onClick={() => toggleSort('customer_ticket')}>티켓<span style={{fontSize:10}}>{arrow('customer_ticket')}</span></div>
        <div style={{ flex: 1, ...hdrClick('symbol') }} onClick={() => toggleSort('symbol')}>📥 트레이더 측 (Limit 등록)<span style={{fontSize:10}}>{arrow('symbol')}</span></div>
        <div style={{ flex: 1, ...hdrClick('limit_order_id') }} onClick={() => toggleSort('limit_order_id')}>📤 마스터 측 (Limit Order)<span style={{fontSize:10}}>{arrow('limit_order_id')}</span></div>
        <div style={{ width: 140, ...hdrClick('opened_at') }} onClick={() => toggleSort('opened_at')}>등록 시각<span style={{fontSize:10}}>{arrow('opened_at')}</span></div>
      </div>
      {sorted.map((r: any, i) => {
        const limitOrderId = r.limit_order_id;
        const isMismatch = r.reconciliation_status === 'MISMATCHED';
        const bgColor = isMismatch ? 'rgba(248,113,113,0.08)' : 'transparent';
        return (
          <div key={i} style={{ ...rowStyle, background: bgColor }}>
            <div style={{ width: 60 }}>{statusBadge(r.reconciliation_status)}</div>
            <div style={{ width: 100, fontFamily: 'monospace', fontSize: 12 }}>
              {r.customer_ticket}
            </div>
            <div style={{ flex: 1 }}>
              <div>{r.symbol} <strong>{r.direction}</strong> {fmtNum(r.lots)} @ {fmtNum(r.customer_open_price, 5)}</div>
              <div style={{ fontSize: 11, color: '#888' }}>
                계좌 {r.account_id} · Limit 가격 {fmtNum((r as any).limit_order_price, 5)} 대기
              </div>
              <div style={{ fontSize: 11, color: '#666' }}>
                timeout: {r.timeout_seconds ?? '-'}s · adverse: {r.adverse_point ?? '-'}pt
              </div>
            </div>
            <div style={{ flex: 1 }}>
              {limitOrderId ? (
                <div>
                  <div style={{ color: '#4ade80' }}>
                    📌 Limit 등록됨
                  </div>
                  <div style={{ fontSize: 11, color: '#888', fontFamily: 'monospace' }}>
                    orderId: {limitOrderId}
                  </div>
                </div>
              ) : (() => {
                // limit_order_id 는 등록 직후 잠깐 NULL (C# 응답 지연)
                // 30초 이내면 "등록 중", 초과하면 진짜 "실패"
                const openedTime = r.opened_at ? new Date(r.opened_at.replace(' ', 'T')).getTime() : 0;
                const elapsed = openedTime ? (Date.now() - openedTime) / 1000 : 0;
                const isRecent = elapsed < 30;
                return isRecent ? (
                  <div style={{ color: '#fbbf24' }}>
                    ⏳ Limit 등록 중
                    <div style={{ fontSize: 11, color: '#888' }}>
                      C# 응답 대기 ({Math.floor(elapsed)}초)
                    </div>
                  </div>
                ) : (
                  <div style={{ fontStyle: 'italic', color: '#f87171' }}>
                    ❌ Limit 등록 실패
                    <div style={{ fontSize: 11 }}>30초 초과 · limit_order_id NULL</div>
                  </div>
                );
              })()}
            </div>
            <div style={{ width: 140, fontSize: 11, color: '#888' }}>
              {fmtDate(r.opened_at)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── 오픈 포지션 테이블 (실시간 + 깜박임) ──────────────────────────────
const PositionTable: React.FC<{
  rows: PositionRow[];
  fmtNum: (v: any, d?: number) => string;
  fmtDate: (s?: string) => string;
  statusBadge: (s: string) => React.ReactNode;
  sortKey: string | null;
  sortDir: 'asc' | 'desc' | null;
  toggleSort: (k: string) => void;
}> = ({ rows, fmtNum, fmtDate, statusBadge, sortKey, sortDir, toggleSort }) => {
  // broker_floating 계산 (표시용)
  const sorted = React.useMemo(() => rows.map(r => {
    const cp = r.customer_floating_profit;
    const mp = r.mop_profit;
    const broker_floating = (cp != null && mp != null) ? (Number(mp) - Number(cp)) : null;
    return { ...r, broker_floating };
  }), [rows]);

  if (rows.length === 0) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>데이터가 없습니다</div>;
  }

  const fmtPnl = (v: number) => v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtPrice = (v: number) => v.toLocaleString(undefined, { minimumFractionDigits: 5, maximumFractionDigits: 5 });
  const sp = { currentKey: sortKey, currentDir: sortDir, onSort: toggleSort };

  return (
    <div style={{ background: '#1a1f3a', borderRadius: 6, overflow: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#0f0f1a', color: '#aaa', fontSize: 12 }}>
            <SortableTh label="상태" sortField="reconciliation_status" {...sp} />
            <SortableTh label="계좌" sortField="account_id" {...sp} />
            <SortableTh label="티켓" sortField="customer_ticket" {...sp} />
            <SortableTh label="심볼" sortField="symbol" {...sp} />
            <SortableTh label="Lots" sortField="lots" align="right" {...sp} />
            <SortableTh label="진입가" sortField="customer_open_price" align="right" {...sp} />
            <SortableTh label="마스터 진입가" sortField="master_open_price" align="right" {...sp} />
            <SortableTh label="갭" sortField="entry_gap_point" align="right" {...sp} />
            <SortableTh label="트레이더 PnL" sortField="customer_floating_profit" align="right" {...sp} />
            <SortableTh label="마스터 PnL" sortField="mop_profit" align="right" {...sp} />
            <SortableTh label="Broker 수익" sortField="broker_floating" align="right" {...sp} />
            <th style={thStyle}>코멘트</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => {
            const bgColor =
              r.reconciliation_status === 'MISMATCHED' || r.reconciliation_status === 'MISMATCHED_CLOSED_AT_MASTER' ? 'rgba(248,113,113,0.08)' :
              r.reconciliation_status === 'ZOMBIE' ? 'rgba(251,191,36,0.08)' :
              'transparent';

            const customerProfit = r.customer_floating_profit ?? null;
            const masterProfit = r.mop_profit ?? null;
            // Broker 수익 (floating) = master profit - customer profit (agent_cost 제외, floating 단계)
            const brokerProfit = (customerProfit != null && masterProfit != null)
              ? (masterProfit - customerProfit)
              : null;

            // Lots 표시: PARTIALLY_CLOSED 면 "원래 → 남은"
            const lotsDisplay = r.remaining_lots != null && r.remaining_lots !== r.lots
              ? `${fmtNum(r.lots)} → ${fmtNum(r.remaining_lots)}`
              : fmtNum(r.lots);

            const comment = (r.mop_comment || '').split('|').slice(1).join(' · ') || '-';

            return (
              <tr key={r.our_id || i} style={{ background: bgColor, borderTop: '1px solid rgba(42,47,74,0.5)' }}>
                <td style={tdStyle}>{statusBadge(r.reconciliation_status)}</td>
                <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{r.account_id || '-'}</td>
                <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }}>{r.customer_ticket || r.mop_ticket || '-'}</td>
                <td style={tdStyle}>
                  {r.symbol} <span style={{ color: r.direction === 'BUY' ? '#4ade80' : '#f87171', fontWeight: 600 }}>{r.direction}</span>
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>{lotsDisplay}</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>
                  {r.customer_open_price != null ? fmtPrice(Number(r.customer_open_price)) : '-'}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>
                  {r.master_open_price != null ? fmtPrice(Number(r.master_open_price)) : '-'}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>
                  {r.entry_gap_point != null ? (
                    <span style={{ color: Number(r.entry_gap_point) >= 0 ? '#4ade80' : '#f87171' }}>
                      {Number(r.entry_gap_point) > 0 ? '+' : ''}{Number(r.entry_gap_point).toFixed(0)}
                    </span>
                  ) : '-'}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <FlashValue
                    value={customerProfit != null ? Number(customerProfit) : null}
                    format={fmtPnl}
                    color={customerProfit != null && customerProfit >= 0 ? '#4ade80' : '#f87171'}
                  />
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <FlashValue
                    value={masterProfit != null ? Number(masterProfit) : null}
                    format={fmtPnl}
                    color={masterProfit != null && masterProfit >= 0 ? '#4ade80' : '#f87171'}
                  />
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <FlashValue
                    value={brokerProfit}
                    format={fmtPnl}
                    color={brokerProfit != null && brokerProfit >= 0 ? '#4ade80' : '#f87171'}
                    bold
                  />
                </td>
                <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11, color: '#888', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.mop_comment || ''}>
                  {comment}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ─── 거래 내역 테이블 (오픈 포지션과 동일 구조 + agent_cost) ──────────────────────────────
const DealsTable: React.FC<{
  rows: PositionRow[];
  fmtNum: (v: any, d?: number) => string;
  fmtDate: (s?: string) => string;
  statusBadge: (s: string) => React.ReactNode;
  sortKey: string | null;
  sortDir: 'asc' | 'desc' | null;
  toggleSort: (k: string) => void;
}> = ({ rows, fmtNum, fmtDate, statusBadge, sortKey, sortDir, toggleSort }) => {
  // broker_floating 계산 (표시용)
  const sorted = React.useMemo(() => rows.map(r => {
    const cp = r.customer_floating_profit;
    const mp = r.mop_profit;
    const broker_floating = (cp != null && mp != null) ? (Number(mp) - Number(cp)) : null;
    return { ...r, broker_floating };
  }), [rows]);

  if (rows.length === 0) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>데이터가 없습니다</div>;
  }

  const fmtPnl = (v: number) => v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtPrice = (v: number) => v.toLocaleString(undefined, { minimumFractionDigits: 5, maximumFractionDigits: 5 });
  const sp = { currentKey: sortKey, currentDir: sortDir, onSort: toggleSort };

  return (
    <div style={{ background: '#1a1f3a', borderRadius: 6, overflow: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#0f0f1a', color: '#aaa', fontSize: 12 }}>
            <SortableTh label="상태" sortField="reconciliation_status" {...sp} />
            <SortableTh label="계좌" sortField="account_id" {...sp} />
            <SortableTh label="티켓" sortField="customer_ticket" {...sp} />
            <SortableTh label="심볼" sortField="symbol" {...sp} />
            <SortableTh label="Lots" sortField="lots" align="right" {...sp} />
            <SortableTh label="진입가" sortField="customer_open_price" align="right" {...sp} />
            <SortableTh label="마스터 진입가" sortField="master_open_price" align="right" {...sp} />
            <SortableTh label="갭" sortField="entry_gap_point" align="right" {...sp} />
            <SortableTh label="트레이더 PnL" sortField="customer_profit" align="right" {...sp} />
            <SortableTh label="마스터 PnL" sortField="master_profit" align="right" {...sp} />
            <SortableTh label="Agent" sortField="agent_cost" align="right" {...sp} />
            <SortableTh label="Broker 수익" sortField="broker_profit" align="right" {...sp} />
            <SortableTh label="청산 시각" sortField="closed_at" {...sp} />
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => {
            const bgColor =
              r.reconciliation_status === 'MISMATCHED' ? 'rgba(248,113,113,0.08)' :
              r.reconciliation_status === 'EXCLUDED' ? 'rgba(128,128,128,0.04)' :
              'transparent';
            const opacity = r.reconciliation_status === 'EXCLUDED' ? 0.55 : 1;

            return (
              <tr key={r.our_id || i} style={{ background: bgColor, opacity, borderTop: '1px solid rgba(42,47,74,0.5)' }}>
                <td style={tdStyle}>{statusBadge(r.reconciliation_status)}</td>
                <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{r.account_id || '-'}</td>
                <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }}>{r.customer_ticket || '-'}</td>
                <td style={tdStyle}>
                  {r.symbol} <span style={{ color: r.direction === 'BUY' ? '#4ade80' : '#f87171', fontWeight: 600 }}>{r.direction}</span>
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>{fmtNum(r.lots)}</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>
                  {r.customer_open_price != null ? fmtPrice(Number(r.customer_open_price)) : '-'}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>
                  {r.master_open_price != null ? fmtPrice(Number(r.master_open_price)) : '-'}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>
                  {r.entry_gap_point != null ? (
                    <span style={{ color: Number(r.entry_gap_point) >= 0 ? '#4ade80' : '#f87171' }}>
                      {Number(r.entry_gap_point) > 0 ? '+' : ''}{Number(r.entry_gap_point).toFixed(0)}
                    </span>
                  ) : '-'}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>
                  {r.customer_profit != null ? (
                    <span style={{ color: Number(r.customer_profit) >= 0 ? '#4ade80' : '#f87171' }}>
                      {fmtPnl(Number(r.customer_profit))}
                    </span>
                  ) : '-'}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>
                  {r.master_profit != null ? (
                    <span style={{ color: Number(r.master_profit) >= 0 ? '#4ade80' : '#f87171' }}>
                      {fmtPnl(Number(r.master_profit))}
                    </span>
                  ) : '-'}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>
                  {r.agent_cost != null ? (
                    <span style={{ color: '#fbbf24' }}>{fmtPnl(Number(r.agent_cost))}</span>
                  ) : '-'}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                  {r.broker_profit != null ? (
                    <span style={{ color: Number(r.broker_profit) >= 0 ? '#4ade80' : '#f87171' }}>
                      {fmtPnl(Number(r.broker_profit))}
                    </span>
                  ) : '-'}
                </td>
                <td style={{ ...tdStyle, fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>
                  {fmtDate(r.closed_at)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ─── 좀비 테이블 ──────────────────────────────
const ZombieTable: React.FC<{
  rows: ZombieRow[];
  fmtNum: (v: any, d?: number) => string;
  fmtDate: (s?: string) => string;
  sortKey: string | null;
  sortDir: 'asc' | 'desc' | null;
  toggleSort: (k: string) => void;
}> = ({ rows, fmtNum, fmtDate, sortKey, sortDir, toggleSort }) => {
  const sorted = rows; // 서버 정렬됨

  if (rows.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#4ade80' }}>
        ✅ 좀비 포지션이 없습니다 (시스템 정상)
      </div>
    );
  }
  const hc = (k: string): React.CSSProperties => ({ cursor: 'pointer', userSelect: 'none', color: sortKey === k ? '#6c8eef' : undefined });
  const ar = (k: string) => sortKey === k ? (sortDir === 'asc' ? ' ▲' : sortDir === 'desc' ? ' ▼' : '') : ' ⇅';
  return (
    <div style={{ background: '#1a1f3a', borderRadius: 6, overflow: 'hidden' }}>
      <div style={{ ...headerRowStyle, background: 'rgba(251,191,36,0.15)' }}>
        <div style={{ width: 100, ...hc('ticket') }} onClick={() => toggleSort('ticket')}>티켓<span style={{fontSize:10}}>{ar('ticket')}</span></div>
        <div style={{ width: 100, ...hc('master_account_id') }} onClick={() => toggleSort('master_account_id')}>마스터/추정계좌<span style={{fontSize:10}}>{ar('master_account_id')}</span></div>
        <div style={{ flex: 1, ...hc('symbol') }} onClick={() => toggleSort('symbol')}>포지션<span style={{fontSize:10}}>{ar('symbol')}</span></div>
        <div style={{ width: 280 }}>코멘트 (원본)</div>
        <div style={{ width: 100, textAlign: 'right', ...hc('profit') }} onClick={() => toggleSort('profit')}>현재 손익<span style={{fontSize:10}}>{ar('profit')}</span></div>
        <div style={{ width: 140, ...hc('open_time') }} onClick={() => toggleSort('open_time')}>진입 시각<span style={{fontSize:10}}>{ar('open_time')}</span></div>
      </div>
      {sorted.map((r, i) => (
        <div key={i} style={{ ...rowStyle, background: 'rgba(251,191,36,0.06)' }}>
          <div style={{ width: 100, fontFamily: 'monospace', fontSize: 12, color: '#fbbf24' }}>{r.ticket}</div>
          <div style={{ width: 100, fontSize: 12 }}>
            <div>{r.master_account_id}</div>
            <div style={{ color: '#888' }}>{r.extracted_account_id || '?'}</div>
          </div>
          <div style={{ flex: 1 }}>
            <div>{r.symbol} <strong>{r.direction}</strong> {fmtNum(r.lots)} @ {fmtNum(r.open_price, 5)}</div>
            <div style={{ fontSize: 11, color: '#888' }}>
              현재가 {fmtNum(r.current_price, 5)} · {r.extracted_tag || '-'}
            </div>
          </div>
          <div style={{ width: 280, fontFamily: 'monospace', fontSize: 11, color: '#aaa', wordBreak: 'break-all' }}>
            {r.comment || '-'}
          </div>
          <div style={{ width: 100, textAlign: 'right', fontFamily: 'monospace', fontSize: 12 }}>
            <span style={{ color: Number(r.profit) >= 0 ? '#4ade80' : '#f87171' }}>
              {fmtNum(r.profit)}
            </span>
          </div>
          <div style={{ width: 140, fontSize: 11, color: '#888' }}>
            {fmtDate(r.open_time)}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── 공통 스타일 ──────────────────────────────
const selectStyle: React.CSSProperties = {
  background: '#0f0f1a', color: '#e0e0e0',
  border: '1px solid #2a2f4a', borderRadius: 4,
  padding: '6px 12px', fontSize: 13
};
const inputStyle: React.CSSProperties = {
  background: '#0f0f1a', color: '#e0e0e0',
  border: '1px solid #2a2f4a', borderRadius: 4,
  padding: '6px 12px', fontSize: 13
};
const headerRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center',
  padding: '12px 16px', background: '#0f0f1a',
  borderBottom: '1px solid #2a2f4a',
  fontSize: 12, fontWeight: 500, color: '#aaa',
  gap: 12
};
const rowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center',
  padding: '12px 16px',
  borderBottom: '1px solid rgba(42,47,74,0.5)',
  fontSize: 13,
  gap: 12
};
const pnlCardStyle: React.CSSProperties = {
  background: '#1a1f3a', padding: '14px 18px', borderRadius: 6
};
const pnlCardLabel: React.CSSProperties = {
  fontSize: 12, color: '#888', marginBottom: 8,
  display: 'flex', alignItems: 'center', gap: 6
};
const liveBadge: React.CSSProperties = {
  fontSize: 10, color: '#4ade80', fontWeight: 600,
  marginLeft: 8, letterSpacing: 0.5
};
const thStyle: React.CSSProperties = {
  padding: '10px 12px', textAlign: 'left', fontWeight: 500,
  borderBottom: '1px solid #2a2f4a', whiteSpace: 'nowrap'
};
const tdStyle: React.CSSProperties = {
  padding: '10px 12px', whiteSpace: 'nowrap'
};
const pageBtnStyle: React.CSSProperties = {
  background: '#1a1f3a', color: '#e0e0e0',
  border: '1px solid #2a2f4a', borderRadius: 4,
  padding: '6px 12px', cursor: 'pointer', fontSize: 13
};

export default ReconciliationPage;
